import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { loginApi, adminLoginApi, logoutApi, validateToken } from '../../services/auth/authApi';
import { clearUserInfo, refreshUserInfo } from '../../hooks/useUserInfo';
import { clearRedirectPath } from '../../utils/redirectUtils';
import type { LoginRequest, User } from '../../services/auth/authApi';

// 타입 정의
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

// 초기 상태
const getInitialAuthState = (): AuthState => {
  const token = localStorage.getItem('access_token');
  return {
    isAuthenticated: !!token,
    user: null,
    token: token,
    loading: false,
    error: null,
  };
};

const initialState: AuthState = getInitialAuthState();

// 비동기 액션
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, rememberMe = false }: { email: string; password: string; rememberMe?: boolean }, { rejectWithValue }) => {
    try {
      const loginData: LoginRequest = { email, password, rememberMe };
      const response = await loginApi(loginData);
      
      // 사용자 정보는 토큰에서 디코딩하거나 별도 API로 가져와야 할 수 있음
      // 임시로 기본 사용자 정보 설정
      const user: User = {
        id: '', // 실제로는 JWT 토큰에서 추출하거나 별도 API 호출
        email: email,
        name: '', // 추후 프로필 API에서 가져옴
        role: 'student',
      };
      
      return {
        token: response.data.access_token,
        user: user,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || '로그인에 실패했습니다');
    }
  }
);

export const adminLogin = createAsyncThunk(
  'auth/adminLogin',
  async ({ email, password, rememberMe = false }: { email: string; password: string; rememberMe?: boolean }, { rejectWithValue }) => {
    try {
      const loginData: LoginRequest = { email, password, rememberMe };
      const response = await adminLoginApi(loginData);
      
      // 관리자 사용자 정보 설정
      const user: User = {
        id: '', // 실제로는 JWT 토큰에서 추출하거나 별도 API 호출
        email: email,
        name: '', // 추후 프로필 API에서 가져옴
        role: 'ADMIN',
      };
      
      return {
        token: response.data.access_token,
        user: user,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || '관리자 로그인에 실패했습니다');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (
    { email, password, name }: { email: string; password: string; name: string },
    { rejectWithValue }
  ) => {
    try {
      // 회원가입 API는 추후 구현 예정
      // const response = await registerApi({ email, password, name });
      // return response;
      
      // 임시로 성공 메시지 반환
      return { message: '회원가입 기능은 준비 중입니다.' };
    } catch (error: any) {
      return rejectWithValue(error.message || '회원가입에 실패했습니다');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await logoutApi();
  // 전역 사용자 정보도 클리어
  clearUserInfo();
  return true;
});

// export const fetchUserProfile = createAsyncThunk(
//   'auth/fetchUserProfile',
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await fetchUserProfileApi();
//       return response;
//     } catch (error: any) {
//       return rejectWithValue(error.message || '프로필 로딩에 실패했습니다');
//     }
//   }
// );

export const checkAuthStatus = createAsyncThunk(
  'auth/checkAuthStatus',
  async (_, { rejectWithValue }) => {
    try {
      const isValid = await validateToken();
      if (!isValid) {
        localStorage.removeItem('access_token');
        throw new Error('토큰이 유효하지 않습니다');
      }
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || '인증 상태 확인에 실패했습니다');
    }
  }
);

// 슬라이스 생성
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      
      // localStorage에도 토큰 저장 (Redux와 동기화)
      localStorage.setItem('access_token', action.payload.token);
      
      // /// console.log('AuthSlice - Login fulfilled, will refresh user info');
      
      // 로그인 성공 시 사용자 정보 즉시 새로고침
      // 비동기로 실행하되 에러는 무시 (LoginModal에서도 호출하므로)
      refreshUserInfo().catch((error) => {
        // /// console.log('AuthSlice - User info refresh failed (will be retried by LoginModal):', error);
      });
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string || '로그인에 실패했습니다';
    });

    // Admin Login
    builder.addCase(adminLogin.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(adminLogin.fulfilled, (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      
      // localStorage에도 토큰 저장 (Redux와 동기화)
      localStorage.setItem('access_token', action.payload.token);
      
      // /// console.log('AuthSlice - Admin Login fulfilled, will refresh user info');
      
      // 관리자 로그인 성공 시 사용자 정보 즉시 새로고침
      refreshUserInfo().catch((error) => {
        // /// console.log('AuthSlice - Admin user info refresh failed:', error);
      });
    });
    builder.addCase(adminLogin.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string || '관리자 로그인에 실패했습니다';
    });

    // Register
    builder.addCase(register.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(register.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string || '회원가입에 실패했습니다';
    });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      // 로그아웃 시 저장된 리다이렉트 경로도 클리어
      clearRedirectPath();
    });

    // Fetch User Profile
    // builder.addCase(fetchUserProfile.pending, (state) => {
    //   state.loading = true;
    // });
    // builder.addCase(fetchUserProfile.fulfilled, (state, action: PayloadAction<User>) => {
    //   state.loading = false;
    //   state.user = action.payload;
    //   state.isAuthenticated = true;
    // });
    // builder.addCase(fetchUserProfile.rejected, (state, action) => {
    //   state.loading = false;
    //   state.error = action.payload as string || '프로필 로딩에 실패했습니다';
    //   state.isAuthenticated = false;
    //   state.user = null;
    //   state.token = null;
    //   localStorage.removeItem('access_token');
    // });

    // Check Auth Status
    builder.addCase(checkAuthStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(checkAuthStatus.fulfilled, (state) => {
      state.loading = false;
      state.isAuthenticated = true;
    });
    builder.addCase(checkAuthStatus.rejected, (state) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
    });
  },
});

export const { resetAuthError } = authSlice.actions;
export default authSlice.reducer;